import { MainWindow } from '../chat/types';
import { Nullable } from '../types';
import { fetchWidgetConfig, initCalendlyInline, loadCalendlyScript } from '../chat/calendly';
import { sendTextToVoiceAgent } from './eleven-labs';
import { saveVoiceLeadAPI } from './api';
import { disconnectVoiceNow, startVoiceSession, stopVoiceSession } from './speech';
import { sendAvatarUserMessage, startAvatarSession, stopAvatarSession } from './liveavatar-avatar';
import { templateTextWidget } from './template';

import { VoiceUIElements } from './types';

type LeadDetailsParseResult =
  | { lead: { name: string; email: string; country: string }; error?: never }
  | { error: string; lead?: never };

const widget = document.createElement('template');
widget.innerHTML = templateTextWidget;

class WitzoVoiceElement extends HTMLElement {
  /**API URL for the backend */
  _apiUrl: Nullable<string> = null;

  /**Unique ID (widget_key) for this widget */
  _uniqueId: Nullable<string> = null;
  _apiBaseUrl: Nullable<string> = null;

  /**Reference for elements */
  chat: Nullable<VoiceUIElements> = null;

  /**Is Initialized */
  isInitialized: boolean = false;

  /**Checks if mic is on or not */
  isMicActive: boolean = false;
  isConversationStarted: boolean = false;
  private isCollectingLeadDetails: boolean = false;
  private leadSessionId: string = createSessionId();
  private calendlyUrl: Nullable<string> = null;
  private calendlyInitialized: boolean = false;
  private avatarEnabled: boolean = false;
  _mainWindow: MainWindow;

  constructor() {
    super();
    const node = widget.content.cloneNode(true);
    //Assume no outside influence on the styling should be possible
    const root = this.attachShadow({ mode: 'open' });
    root.appendChild(node);

    this.getElements(root);

    this._mainWindow = {
      back: () => {},
      close: () => {},
    };

    if (this.chat) {
      this.chat.input.disabled = true;
      this.chat.input.placeholder = 'Start Conversation to enter details ';
    }
  }

  /**
   * @description retrieve elements from shadow dom and ensures initialization
   */
  private getElements(root: ShadowRoot) {
    const q = <T extends HTMLElement | SVGSVGElement|SVGPathElement>(selector: string) =>
      root.querySelector(selector) as T;

    try {
      this.chat = {
        backButton: q('#backToSelection'),
        chatWindow: q('#chatWidget'),
        closeButton: q('#closeVoiceChat'),
        calendlyButton: q('#voiceCalendlyButton'),
        calendlyButtonWrap: q('#voiceCalendlyButtonWrap'),
        calendlyContainer: q('#voiceCalendlyContainer'),
        calendlyCloseButton: q('#voiceCalendlyCloseButton'),
        calendlyWidgetWrapper: q('#voiceCalendlyWidgetWrapper'),
        micButton: q('#voiceMicButton'),
        messagesContainer: q('#voiceTranscript'),
        endButton: q('#endVoiceCall'),
        status: q('#voiceStatusText'),
        waves: {
          root: q('#voiceVisualizerBars'),
          wavelets: Array.from(root.querySelectorAll('.voice-wave')) as HTMLDivElement[],
        },
        sendButton: q('#textSendButton'),
        input: q('#textMessageInput'),
        icons: {
          startChat: q('#start-icon'),
          endChat: q('#end-icon'),

          micStartIcon:q('#mic-start-icon'),
          micEndIcon:q('#mic-end-icon'),
        },
        bannerText: q('#voice-banner-text'),
        bannerTextParagraph: q('#voice-banner-text-paragraph'),
        chatHeader: q('#voice-header'),
        closeIcon: q('#voice-close-button-icon'),
        logoIcon: q('#voiceLogoIcon'),
        defaultIcon: q('#voiceDefaultIcon'),
        voiceInterface: q('#voiceInterface'),
        avatar: {
          section: q('#voiceAvatarSection'),
          video: q('#voiceAvatarVideo'),
          loading: q('#voiceAvatarLoading'),
        },
      };
      this.isInitialized = true;

      this.chat.icons.micStartIcon.style.fill='';
      this.chat.icons.micEndIcon.style.fill='';
    } catch (err) {
      this.isInitialized = false;
      console.warn(`Couldn't initialize ${err}`);
    }
  }

  /**
   * @description set the API URL for backend communication
   */
  set apiUrl(url: string) {
    this._apiUrl = url;
    if (this.isConnected) void this.initializeCalendly();
  }

  set apiBaseURL(url: string) {
    this._apiBaseUrl = url;
    if (this.isConnected) void this.initializeCalendly();
  }

  /**
   * @description set the unique ID (widget_key) for this widget
   */
  set uniqueId(id: string) {
    this._uniqueId = id;
    if (this.isConnected) void this.initializeCalendly();
  }

  /**
   * @deprecated Use apiUrl and uniqueId instead. Kept for backward compatibility.
   */
  set agentId(id: string) {
    // No-op: agent-id is now managed by the backend via widget configuration
  }

  set mainWindow(_mainWindow: MainWindow) {
    this._mainWindow = _mainWindow;
  }

  set iconColors(icons:{
    iconColors:Nullable<string>
  }){
    if(!this.chat) return;

    this.chat.micButton.style.borderColor=icons.iconColors ?? '#FC0E3F'
    this.chat.micButton.style.boxShadow=`0 0 16px ${icons.iconColors ?? '#FC0E3F'}`
    this.chat.icons.micStartIcon.style.fill=icons.iconColors ?? '#FC0E3F';
    this.chat.icons.micEndIcon.style.stroke=icons.iconColors ?? '#FC0E3F';
    this.chat.sendButton.style.backgroundColor=icons.iconColors ?? '#FC0E3F';
    this.chat.micButton.style.setProperty('--pulse-color',icons.iconColors)
  }

  set bannerText(text: string) {
    if (this.chat?.bannerText) this.chat.bannerText.textContent = text;
  }

  set bannerTextColor(colorValue: string) {
    if (this.chat?.bannerText) this.chat.bannerText.style.color = colorValue;
  }

  set bannerTextParagraph(text: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.textContent = text;
  }

  set bannerTextParagraphColor(colorValue: string) {
    if (this.chat?.bannerTextParagraph) this.chat.bannerTextParagraph.style.color = colorValue;
  }

  set chatHeader(backgroundColor: string) {
    if (this.chat?.chatHeader) this.chat.chatHeader.style.backgroundColor = backgroundColor;
    if (this.chat?.closeButton) this.chat.closeButton.style.backgroundColor = backgroundColor;
    if (this.chat?.backButton) (this.chat.backButton as HTMLElement).style.backgroundColor = backgroundColor;
  }

  set logoIcon(iconUrl: string) {
    if (this.chat?.logoIcon) {
      this.chat.logoIcon.src = iconUrl;
      this.chat.logoIcon.style.display = '';
      if (this.chat.defaultIcon) this.chat.defaultIcon.style.display = 'none';
    }
  }

  set closeButtonColor(colorValue: string) {
    if (this.chat?.closeIcon) (this.chat.closeIcon as HTMLElement).style.fill = colorValue;
  }

  connectedCallback() {
    this.bindEvents();
    void this.initializeCalendly();
  }

  bindEvents() {
    if (!this.chat || this.isInitialized === false) return;
    const {
      backButton,
      chatWindow,
      closeButton,
      calendlyButton,
      calendlyCloseButton,
      endButton,
      micButton,
      status,
      waves,
      sendButton,
      input,
    } = this.chat;

    backButton.addEventListener('click', async () => {
      await this.teardownActiveSession();
      this.resetVoiceUIState();
      if (this._mainWindow) this._mainWindow.back();
    });

    closeButton.addEventListener('click', async () => {
      this.closeCalendly();
      await this.teardownActiveSession();
      this.resetVoiceUIState();
      this._mainWindow.close();
    });

    calendlyButton.addEventListener('click', async () => {
      await this.disconnectVoiceForBooking();
      this.openCalendly();
    });

    calendlyCloseButton.addEventListener('click', () => {
      this.closeCalendly();
    });

    micButton.addEventListener('click', async () => await this.toggleMic());

    sendButton.addEventListener('click', () => {
      if (this.isCollectingLeadDetails) {
        void this.submitLeadDetails();
        return;
      }

      // Avatar mode: LiveAvatar hosts the ElevenLabs agent, so send the typed
      // message through the LiveAvatar session instead of the @elevenlabs/client path.
      if (this.avatarEnabled && this.isMicActive) {
        const text = input.value.trim();
        if (text) {
          this.appendTranscriptMessage('user', text);
          sendAvatarUserMessage(text);
          input.value = '';
        }
        return;
      }

      sendTextToVoiceAgent(input, this.chat!.messagesContainer, status, () => this.disconnectForCareerIntent());
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendButton.click();
      }
    });
  }

  private resetVoiceUIState() {
    if (!this.chat) return;
    this.isMicActive = false;
    this.isCollectingLeadDetails = false;
    this.chat.micButton.classList.remove('active');
    this.chat.micButton.disabled = false;
    this.chat.icons.startChat.classList.remove('hidden');
    this.chat.icons.endChat.classList.add('hidden');
    this.chat.input.disabled = true;
    this.chat.input.placeholder = 'Start Conversation to enter details ';
    this.chat.input.classList.add('chat-visibility');
    this.chat.sendButton.classList.add('chat-visibility');
  }

  private appendTranscriptMessage(source: string, message: string) {
    if (!this.chat) return;

    const { messagesContainer } = this.chat;
    const firstText = messagesContainer.textContent?.trim() || '';
    if (firstText === 'Your conversation will appear here in real-time...') {
      messagesContainer.innerHTML = '';
    }

    const paragraph = document.createElement('p');
    const strong = document.createElement('strong');
    strong.textContent = source;
    paragraph.appendChild(strong);
    paragraph.appendChild(document.createTextNode(` : ${message}`));
    messagesContainer.appendChild(paragraph);
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  private enableLeadDetailsInput(message: string) {
    if (!this.chat) return;

    const { input, sendButton, status } = this.chat;
    this.isCollectingLeadDetails = true;
    status.textContent = 'Please enter your details below.';
    this.appendTranscriptMessage('assistant', message);
    input.disabled = false;
    input.placeholder = 'Name, Email, Country';
    input.classList.remove('chat-visibility');
    sendButton.classList.remove('chat-visibility');
    input.focus();
  }

  private parseLeadDetails(value: string): LeadDetailsParseResult {
    const parts = value
      .split(/,|\n/)
      .map((part) => part.trim())
      .filter(Boolean);

    if (parts.length !== 3) {
      return {
        error: 'Please provide details in this format: Name, Email, Country',
      };
    }

    const [name, email, country] = parts;
    const errors: string[] = [];

    if (name.length < 2) errors.push('valid Name');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.push('valid Email');
    if (country.length < 2) errors.push('valid Country');

    if (errors.length > 0) {
      return {
        error: `Please provide ${errors.join(' and ')}. Format: Name, Email, Country`,
      };
    }

    return {
      lead: {
        name,
        email,
        country,
      },
    };
  }

  private async submitLeadDetails() {
    if (!this._apiUrl || !this._uniqueId || !this.chat) return;

    const { input, sendButton, status } = this.chat;
    const rawValue = input.value.trim();
    if (!rawValue) return;

    this.appendTranscriptMessage('user', rawValue);
    input.value = '';

    const parsed = this.parseLeadDetails(rawValue);
    if (parsed.error) {
      this.appendTranscriptMessage('assistant', parsed.error);
      return;
    }
    if (!parsed.lead) return;

    input.disabled = true;
    sendButton.disabled = true;
    status.textContent = 'Saving details...';

    try {
      await saveVoiceLeadAPI(this._apiUrl, this._uniqueId, this.leadSessionId, parsed.lead);
      this.appendTranscriptMessage('assistant', 'Thank you! We have received your details and will contact you shortly.');
      status.textContent = 'Details received.';
      input.classList.add('chat-visibility');
      sendButton.classList.add('chat-visibility');
      this.isCollectingLeadDetails = false;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to save details. Please try again.';
      this.appendTranscriptMessage('assistant', message);
      status.textContent = 'Unable to save details.';
      input.disabled = false;
    } finally {
      sendButton.disabled = false;
    }
  }

  private setMicUIState(active: boolean) {
    if (!this.chat) return;

    const { status } = this.chat;
    status.textContent = active ? 'Establishing Connection...' : 'Terminating Connection...';
  }

  private getResolvedApiBaseUrl() {
    const explicitBase = (this._apiBaseUrl || '').trim();
    if (explicitBase) return explicitBase.replace(/\/$/, '');

    const apiUrl = this._apiUrl || '';
    try {
      const parsedUrl = new URL(apiUrl);
      const apiV1Index = parsedUrl.pathname.indexOf('/api/v1');
      if (apiV1Index >= 0) {
        return `${parsedUrl.origin}${parsedUrl.pathname.slice(0, apiV1Index + '/api/v1'.length)}`;
      }
      return parsedUrl.origin + '/api/v1';
    } catch {
      const normalized = apiUrl.trim().replace(/\/$/, '');
      const apiV1Index = normalized.indexOf('/api/v1');
      if (apiV1Index >= 0) {
        return normalized.slice(0, apiV1Index + '/api/v1'.length);
      }
      return normalized.replace(/\/webhook(?:\/.*)?$/, '') + '/api/v1';
    }
  }

  private async initializeCalendly() {
    if (!this._apiUrl || !this._uniqueId || !this.chat) return;
    if (this.calendlyInitialized) return;

    try {
      const apiBaseUrl = this.getResolvedApiBaseUrl();
      const config = await fetchWidgetConfig(apiBaseUrl, this._uniqueId);

      if (config.has_calendly && config.calendly_url) {
        this.calendlyUrl = config.calendly_url;
        this.chat.calendlyButtonWrap.classList.remove('hidden');
        await loadCalendlyScript();
      }

      // Backend already gates avatar_enabled on a configured avatar id + voice support.
      // NOTE: this only marks the widget as avatar-capable. The taller "avatar" layout is
      // applied per-session (see setAvatarLayout) so the widget stays small while idle.
      this.avatarEnabled = config.avatar_enabled === true && !!config.liveavatar_avatar_id;

      this.calendlyInitialized = true;
    } catch (error) {
      console.error('[witzo-voice] Failed to initialize Calendly:', error);
    }
  }

  private openCalendly() {
    if (!this.calendlyUrl || !this.chat) return;

    this.chat.calendlyContainer.classList.add('active');
    this.chat.calendlyWidgetWrapper.innerHTML = '';
    initCalendlyInline(this.chat.calendlyWidgetWrapper, this.calendlyUrl);
    this.dispatchEvent(
      new CustomEvent('witzo-widget-layout-change', {
        detail: { mode: 'calendar' },
        bubbles: true,
        composed: true,
      })
    );
  }

  private closeCalendly() {
    if (!this.chat) return;

    this.chat.calendlyContainer.classList.remove('active');
    this.dispatchEvent(
      new CustomEvent('witzo-widget-layout-change', {
        detail: { mode: 'default' },
        bubbles: true,
        composed: true,
      })
    );
  }

  private async disconnectVoiceForBooking() {
    if (!this.chat || !this.isMicActive) return;

    const { status, waves, input, icons, sendButton, micButton } = this.chat;

    input.disabled = true;
    input.placeholder = 'Start Conversation to Enter Details ';
    this.setMicUIState(false);

    try {
      if (this.avatarEnabled) {
        await stopAvatarSession();
      } else {
        await stopVoiceSession(status, waves.root);
      }
    } catch (error) {
      console.warn('[witzo-voice] Failed to stop voice before booking:', error);
    }

    micButton.classList.remove('active');
    micButton.disabled = false;
    icons.startChat.classList.remove('hidden');
    icons.endChat.classList.add('hidden');
    input.classList.add('chat-visibility');
    sendButton.classList.add('chat-visibility');
    this.isMicActive = false;
  }

  private async disconnectForCareerIntent() {
    if (!this.chat || !this.isMicActive) return;

    const { status, waves, input, icons, sendButton, micButton } = this.chat;

    try {
      await stopVoiceSession(status, waves.root);
    } catch (error) {
      console.warn('[witzo-voice] Failed to stop voice after career intent:', error);
    }

    status.textContent = 'Disconnected';
    micButton.classList.remove('active');
    micButton.disabled = false;
    icons.startChat.classList.remove('hidden');
    icons.endChat.classList.add('hidden');
    input.disabled = true;
    input.placeholder = 'Start Conversation to enter details ';
    input.classList.add('chat-visibility');
    sendButton.classList.add('chat-visibility');
    this.isMicActive = false;
  }

  /**
   * Enter/exit the taller avatar layout. Applied only while an avatar session is live,
   * so an avatar-capable widget still shows small when idle. Toggles the interface
   * layout class and asks the outer window to grow/shrink.
   */
  private setAvatarLayout(active: boolean) {
    if (!this.chat) return;
    this.chat.voiceInterface.classList.toggle('avatar-mode', active);
    this.dispatchEvent(
      new CustomEvent('witzo-widget-avatar-mode', { detail: { active }, bubbles: true, composed: true })
    );
  }

  /** Stop whichever voice session is active (LiveAvatar or the plain ElevenLabs path). */
  private async teardownActiveSession() {
    if (!this.chat) return;
    if (this.avatarEnabled) {
      await stopAvatarSession();
      this.setAvatarLayout(false);
    } else {
      await disconnectVoiceNow(this.chat.status, this.chat.waves.root);
    }
  }

  /** Called when a LiveAvatar session ends on its own (server disconnect / max duration). */
  private handleAvatarEnded() {
    if (!this.chat) return;
    this.setAvatarLayout(false);
    this.resetVoiceUIState();
    this.chat.status.textContent = 'Tap to mic to start voice chat';
  }

  async toggleMic() {
    if (!this._apiUrl || !this._uniqueId || !this.chat) return;

    const { micButton, status, waves, messagesContainer, input, icons, sendButton } = this.chat;
    micButton.classList.toggle('active');

    try {
      if (!this.isMicActive) {
        input.disabled = false;
        this.chat.input.placeholder = 'Type your message...';
        micButton.disabled = true;

        this.setMicUIState(true);
        if (this.avatarEnabled) {
          // Avatar mode: the whole conversation is a LiveAvatar session (it hosts the
          // ElevenLabs agent, mic and transcript). The @elevenlabs/client path is unused.
          // Grow the widget + switch to the avatar layout as it connects.
          this.setAvatarLayout(true);
          await startAvatarSession({
            apiUrl: this._apiUrl,
            uniqueId: this._uniqueId,
            els: this.chat.avatar,
            status,
            messagesContainer,
            onEnded: () => this.handleAvatarEnded(),
          });
          micButton.disabled = false; // allow ending the call
        } else {
          await startVoiceSession(
            this._apiUrl,
            this._uniqueId,
            status,
            waves.root,
            waves.wavelets,
            messagesContainer,
            micButton,
            () => this.disconnectForCareerIntent()
          );
        }
        icons.startChat.classList.add('hidden');
        icons.endChat.classList.remove('hidden');
        input.classList.remove('chat-visibility');
        sendButton.classList.remove('chat-visibility');
      } else {
        input.disabled = true;
        this.setMicUIState(false);
        this.chat.input.placeholder = 'Start Conversation to Enter Details ';
        if (this.avatarEnabled) {
          await stopAvatarSession();
          this.setAvatarLayout(false);
          status.textContent = 'Tap to mic to start voice chat';
        } else {
          await stopVoiceSession(status, waves.root);
        }
        icons.startChat.classList.remove('hidden');
        icons.endChat.classList.add('hidden');
        input.classList.add('chat-visibility');
        sendButton.classList.add('chat-visibility');
      }

      this.isMicActive = !this.isMicActive;
    } catch (err: any) {
      console.error('[witzo-voice] Error while mic toggle:', err);
      // If the avatar failed to start, shrink the widget back to its idle size.
      if (this.avatarEnabled) this.setAvatarLayout(false);
      const msg = err?.message || String(err);
      if (msg.includes('secure context') || msg.includes('HTTPS') || msg.includes('localhost')) {
        status.textContent = 'Voice requires HTTPS or localhost.';
      } else if (msg.includes("You've reached today's voice chat limit")) {
        status.textContent = "You've reached today's voice chat limit. Please try again tomorrow.";
      } else if (msg.includes('Name, Email, and Country') || msg.includes('Insufficient credits')) {
        this.enableLeadDetailsInput(msg);
      } else if (msg.includes('Permission') || msg.includes('NotAllowed') || msg.includes('getUserMedia') || msg.includes('Microphone')) {
        status.textContent = 'Microphone access denied. Please allow mic access.';
      } else {
        status.textContent = 'Error Occurred. Please Try Again';
      }
      if (!this.isCollectingLeadDetails) {
        input.disabled = true;
        input.placeholder = 'Start Conversation to enter details ';
        input.classList.add('chat-visibility');
        sendButton.classList.add('chat-visibility');
      }
      icons.startChat.classList.remove('hidden');
      icons.endChat.classList.add('hidden');
      micButton.disabled = false;
      micButton.classList.remove('active');
    }
  }
}

export { WitzoVoiceElement };

function createSessionId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `voice-lead-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
